import { useEffect, useState } from 'react';
import { clienteService, pagoService, prestamoService } from '../../services/api';
import '../../styles/Reportes.css';

const BuroCredito = () => {
  const [clientes, setClientes] = useState([]);
  const [clienteSeleccionado, setClienteSeleccionado] = useState('');
  const [reporte, setReporte] = useState(null);
  const [cargando, setCargando] = useState(false);

  useEffect(() => {
    cargarClientes();
  }, []);

  const cargarClientes = async () => {
    try {
      const response = await clienteService.getAll();
      setClientes(response.data.clientes || []);
    } catch (error) {
      console.error('Error al cargar clientes:', error);
    }
  };

  const generarReporte = async () => {
    if (!clienteSeleccionado) {
      alert('Por favor selecciona un cliente');
      return;
    }

    setCargando(true);
    try {
      const [responseCliente, responsePrestamos, responsePagos] = await Promise.all([
        clienteService.getById(clienteSeleccionado),
        prestamoService.getAll(),
        pagoService.getAll()
      ]);

      const cliente = responseCliente.data;
      const todosPrestamos = responsePrestamos.data.prestamos || [];
      const todosPagos = responsePagos.data.pagos || [];

      // Filtrar préstamos del cliente
      const prestamosCliente = todosPrestamos.filter(p => p.cliente_id == clienteSeleccionado);

      if (prestamosCliente.length === 0) {
        setReporte({
          cliente,
          sinHistorial: true
        });
        setCargando(false);
        return;
      }

      // Calcular métricas crediticias
      const totalPrestamos = prestamosCliente.length;
      const prestamosPagados = prestamosCliente.filter(p => p.estado === 'pagado').length;
      const prestamosActivos = prestamosCliente.filter(p => p.estado === 'activo').length;
      const prestamosAtrasados = prestamosCliente.filter(p => p.estado === 'atrasado').length;
      const prestamosVencidos = prestamosCliente.filter(p => p.estado === 'vencido').length;

      const totalPrestado = prestamosCliente.reduce((sum, p) => sum + p.monto_prestado, 0);
      const totalAPagar = prestamosCliente.reduce((sum, p) => sum + p.total, 0);

      // Calcular pagos realizados
      const idsPrestamosl = prestamosCliente.map(p => p.id);
      const pagosCliente = todosPagos.filter(p => idsPrestamosl.includes(p.prestamo_id));
      const totalPagado = pagosCliente.reduce((sum, p) => sum + p.monto_total, 0);

      // Puntualidad (basado en cuotas a tiempo)
      let cuotasATiempo = 0;
      let cuotasTotales = 0;

      prestamosCliente.forEach(prestamo => {
        const pagosPrestamo = pagosCliente.filter(p => p.prestamo_id === prestamo.id);
        cuotasTotales += prestamo.cuotas_pagadas;

        // Simular puntualidad (en un sistema real se compararía con fechas esperadas)
        if (prestamo.estado === 'activo' || prestamo.estado === 'pagado') {
          cuotasATiempo += prestamo.cuotas_pagadas;
        } else if (prestamo.estado === 'atrasado') {
          cuotasATiempo += Math.floor(prestamo.cuotas_pagadas * 0.7);
        } else {
          cuotasATiempo += Math.floor(prestamo.cuotas_pagadas * 0.3);
        }
      });

      const puntualidad = cuotasTotales > 0 ? (cuotasATiempo / cuotasTotales) * 100 : 0;

      // Calcular score crediticio (0-1000)
      let score = 300; // Base

      // Por pagos completados (+350)
      score += (prestamosPagados / Math.max(totalPrestamos, 1)) * 350;

      // Por puntualidad (+250)
      score += (puntualidad / 100) * 250;

      // Penalización por atrasos y vencidos (-200 max)
      score -= (prestamosAtrasados * 50);
      score -= (prestamosVencidos * 100);

      // Por monto pagado vs debido (+100)
      if (totalAPagar > 0) {
        score += (totalPagado / totalAPagar) * 100;
      }

      score = Math.max(300, Math.min(1000, Math.round(score)));

      // Determinar categoría crediticia
      let categoria, colorCategoria;
      if (score >= 850) {
        categoria = 'Excelente';
        colorCategoria = '#27ae60';
      } else if (score >= 700) {
        categoria = 'Muy Bueno';
        colorCategoria = '#2ecc71';
      } else if (score >= 600) {
        categoria = 'Bueno';
        colorCategoria = '#f39c12';
      } else if (score >= 500) {
        categoria = 'Regular';
        colorCategoria = '#e67e22';
      } else {
        categoria = 'Malo';
        colorCategoria = '#e74c3c';
      }

      // Historial de préstamos
      const historial = prestamosCliente.map(p => {
        const pagosPrestamo = pagosCliente.filter(pago => pago.prestamo_id === p.id);
        const pagadoPrestamo = pagosPrestamo.reduce((sum, pago) => sum + pago.monto_total, 0);

        return {
          ...p,
          totalPagado: pagadoPrestamo,
          porcentajePagado: p.total > 0 ? (pagadoPrestamo / p.total) * 100 : 0
        };
      }).sort((a, b) => new Date(b.fecha_inicio) - new Date(a.fecha_inicio));

      setReporte({
        cliente,
        score,
        categoria,
        colorCategoria,
        totalPrestamos,
        prestamosPagados,
        prestamosActivos,
        prestamosAtrasados,
        prestamosVencidos,
        totalPrestado,
        totalAPagar,
        totalPagado,
        puntualidad: puntualidad.toFixed(1),
        historial,
        sinHistorial: false
      });
    } catch (error) {
      alert('Error al generar reporte');
      console.error(error);
    } finally {
      setCargando(false);
    }
  };

  const formatearFecha = (fecha) => {
    return new Date(fecha).toLocaleDateString('es-DO');
  };

  return (
    <div className="reporte-detalle">
      <div className="reporte-header">
        <h1>📋 Buró de Crédito</h1>
        <p>Historial crediticio y score del cliente</p>
      </div>

      <div className="filtros-container">
        <div className="filtro-grupo">
          <label>Seleccionar Cliente:</label>
          <select
            value={clienteSeleccionado}
            onChange={(e) => setClienteSeleccionado(e.target.value)}
            className="input-text"
          >
            <option value="">-- Seleccione un cliente --</option>
            {clientes.map(cliente => (
              <option key={cliente.id} value={cliente.id}>
                {cliente.nombre} {cliente.apellido} - {cliente.cedula}
              </option>
            ))}
          </select>
        </div>

        <button onClick={generarReporte} className="btn-buscar">
          Generar Reporte
        </button>
      </div>

      {cargando && (
        <div className="loading">Generando reporte crediticio...</div>
      )}

      {!cargando && reporte && reporte.sinHistorial && (
        <div className="sin-datos">
          <p>Este cliente no tiene historial crediticio</p>
        </div>
      )}

      {!cargando && reporte && !reporte.sinHistorial && (
        <>
          <div className="info-prestamo">
            <h3>Cliente: {reporte.cliente.nombre} {reporte.cliente.apellido}</h3>
            <div className="info-grid">
              <div><strong>Cédula:</strong> {reporte.cliente.cedula}</div>
              <div><strong>Teléfono:</strong> {reporte.cliente.telefono}</div>
              <div><strong>Dirección:</strong> {reporte.cliente.direccion}</div>
            </div>
          </div>

          <div className="info-prestamo" style={{
            marginTop: '30px',
            background: `linear-gradient(135deg, ${reporte.colorCategoria}22 0%, ${reporte.colorCategoria}11 100%)`,
            border: `2px solid ${reporte.colorCategoria}`
          }}>
            <div style={{ textAlign: 'center' }}>
              <h2 style={{ color: reporte.colorCategoria, fontSize: '3em', margin: '10px 0' }}>
                {reporte.score}
              </h2>
              <h3 style={{ color: reporte.colorCategoria, margin: '0' }}>
                {reporte.categoria}
              </h3>
              <p style={{ margin: '10px 0', color: '#666' }}>Score Crediticio</p>
            </div>
          </div>

          <div className="resumen-cards">
            <div className="card-resumen">
              <div className="card-icono">📊</div>
              <div className="card-info">
                <div className="card-valor">{reporte.totalPrestamos}</div>
                <div className="card-label">Total Préstamos</div>
              </div>
            </div>

            <div className="card-resumen" style={{ borderLeft: '4px solid #27ae60' }}>
              <div className="card-icono">✅</div>
              <div className="card-info">
                <div className="card-valor" style={{ color: '#27ae60' }}>
                  {reporte.prestamosPagados}
                </div>
                <div className="card-label">Completados</div>
              </div>
            </div>

            <div className="card-resumen" style={{ borderLeft: '4px solid #3498db' }}>
              <div className="card-icono">⏳</div>
              <div className="card-info">
                <div className="card-valor">{reporte.prestamosActivos}</div>
                <div className="card-label">Activos</div>
              </div>
            </div>

            <div className="card-resumen" style={{ borderLeft: '4px solid #e74c3c' }}>
              <div className="card-icono">⚠️</div>
              <div className="card-info">
                <div className="card-valor" style={{ color: '#e74c3c' }}>
                  {reporte.prestamosAtrasados + reporte.prestamosVencidos}
                </div>
                <div className="card-label">Con Problemas</div>
              </div>
            </div>
          </div>

          <div className="info-prestamo" style={{ marginTop: '30px' }}>
            <h3>💵 Resumen Financiero</h3>
            <div className="info-grid">
              <div>
                <strong>Total Prestado:</strong> ${reporte.totalPrestado.toFixed(2)}
              </div>
              <div>
                <strong>Total a Pagar:</strong> ${reporte.totalAPagar.toFixed(2)}
              </div>
              <div>
                <strong>Total Pagado:</strong> ${reporte.totalPagado.toFixed(2)}
              </div>
              <div>
                <strong>Porcentaje Pagado:</strong> {((reporte.totalPagado / reporte.totalAPagar) * 100).toFixed(1)}%
              </div>
            </div>
          </div>

          <div className="info-prestamo" style={{ marginTop: '20px' }}>
            <h3>⏱️ Comportamiento de Pago</h3>
            <div className="info-grid">
              <div>
                <strong>Puntualidad:</strong>
                <span style={{
                  marginLeft: '10px',
                  fontSize: '1.2em',
                  color: reporte.puntualidad >= 80 ? '#27ae60' : reporte.puntualidad >= 60 ? '#f39c12' : '#e74c3c'
                }}>
                  {reporte.puntualidad}%
                </span>
              </div>
              <div>
                <strong>Tasa de Éxito:</strong>
                <span style={{ marginLeft: '10px', fontSize: '1.2em' }}>
                  {((reporte.prestamosPagados / reporte.totalPrestamos) * 100).toFixed(1)}%
                </span>
              </div>
            </div>
          </div>

          <div className="info-prestamo" style={{ marginTop: '30px' }}>
            <h3>📜 Historial de Préstamos</h3>
            <div className="tabla-container">
              <table className="tabla-reporte">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Fecha</th>
                    <th>Monto</th>
                    <th>Total</th>
                    <th>Pagado</th>
                    <th>% Pagado</th>
                    <th>Cuotas</th>
                    <th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {reporte.historial.map(prestamo => (
                    <tr key={prestamo.id}>
                      <td>{prestamo.id}</td>
                      <td>{formatearFecha(prestamo.fecha_inicio)}</td>
                      <td>${prestamo.monto_prestado.toFixed(2)}</td>
                      <td>${prestamo.total.toFixed(2)}</td>
                      <td>${prestamo.totalPagado.toFixed(2)}</td>
                      <td>{prestamo.porcentajePagado.toFixed(1)}%</td>
                      <td>{prestamo.cuotas_pagadas}/{prestamo.cuotas}</td>
                      <td>
                        <span className={`badge badge-${prestamo.estado}`}>
                          {prestamo.estado}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="info-prestamo" style={{
            marginTop: '20px',
            background: '#e3f2fd',
            border: '1px solid #2196f3'
          }}>
            <h3 style={{ color: '#1976d2' }}>ℹ️ Interpretación del Score</h3>
            <ul style={{ lineHeight: '1.8', color: '#0d47a1' }}>
              <li><strong>850-1000 (Excelente):</strong> Cliente de confianza total, aprobación inmediata</li>
              <li><strong>700-849 (Muy Bueno):</strong> Cliente confiable, condiciones favorables</li>
              <li><strong>600-699 (Bueno):</strong> Cliente aceptable, condiciones estándar</li>
              <li><strong>500-599 (Regular):</strong> Requiere análisis adicional y garantías</li>
              <li><strong>300-499 (Malo):</strong> Alto riesgo, no recomendable</li>
            </ul>
          </div>
        </>
      )}

      {!cargando && !reporte && (
        <div className="sin-datos">
          <p>Selecciona un cliente y genera su reporte crediticio</p>
        </div>
      )}
    </div>
  );
};

export default BuroCredito;
